import { CubeCamera, HalfFloatType, LinearFilter, PMREMGenerator, WebGLCubeRenderTarget } from 'three';
import type { Object3D, Scene, Texture, Vector3, WebGLRenderTarget, WebGLRenderer } from 'three';

/**
 * A live reflection probe for the pearl: a small HDR cube map re-rendered around it, one face
 * at a time, then prefiltered (PMREM) so the clear-coat mirrors the real sky, sand and meadow it
 * is rolling through. Heavy, purely decorative objects (grass, particles, the pearl itself) are
 * hidden while it renders, and the shadow maps are not re-rendered for it.
 *
 * The probe owns its PMREM generator and target: handing three the raw cube render target made
 * it cache a PMREM target per texture that was never freed (one leaked per world rebuild).
 */
export class EnvProbe {
  readonly target: WebGLCubeRenderTarget;
  private readonly cube: CubeCamera;
  private face = 0;
  private pmrem: PMREMGenerator | null = null;
  private env: WebGLRenderTarget | null = null;
  private readonly visible: boolean[] = [];

  constructor(size: number) {
    this.target = new WebGLCubeRenderTarget(size, { type: HalfFloatType, generateMipmaps: false });
    this.target.texture.minFilter = LinearFilter;
    this.cube = new CubeCamera(0.3, 1800, this.target);
  }

  /** True once every face has been rendered (and prefiltered) at least once. */
  get ready(): boolean {
    return this.env !== null;
  }

  /** The prefiltered environment (valid once `ready`). */
  get texture(): Texture {
    return this.env!.texture;
  }

  /** Render `faces` faces of the probe at `center`. */
  update(gl: WebGLRenderer, scene: Scene, center: Vector3, hide: readonly Object3D[], faces = 1): void {
    const cube = this.cube;
    if (cube.coordinateSystem !== gl.coordinateSystem) {
      cube.coordinateSystem = gl.coordinateSystem;
      cube.updateCoordinateSystem();
    }
    cube.position.copy(center);
    cube.updateMatrixWorld(true);
    const vis = this.visible;
    for (let i = 0; i < hide.length; i++) {
      vis[i] = hide[i]!.visible;
      hide[i]!.visible = false;
    }
    const prevTarget = gl.getRenderTarget();
    const prevAuto = gl.shadowMap.autoUpdate;
    const prevXr = gl.xr.enabled;
    gl.shadowMap.autoUpdate = false;
    gl.xr.enabled = false;
    let done = false;
    for (let k = 0; k < faces; k++) {
      gl.setRenderTarget(this.target, this.face);
      gl.render(scene, cube.children[this.face] as never);
      this.face = (this.face + 1) % 6;
      if (this.face === 0) done = true;
    }
    gl.setRenderTarget(prevTarget);
    gl.shadowMap.autoUpdate = prevAuto;
    gl.xr.enabled = prevXr;
    for (let i = 0; i < hide.length; i++) hide[i]!.visible = vis[i]!;
    if (done) {
      this.pmrem ??= new PMREMGenerator(gl);
      this.env = this.pmrem.fromCubemap(this.target.texture, this.env);
    }
  }

  dispose(): void {
    this.target.dispose();
    this.env?.dispose();
    this.pmrem?.dispose();
  }
}
